import { Link } from '@chakra-ui/react';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Trans, useTranslation } from 'react-i18next';

import { externalRoutes } from '../routes/routesConfig';
import { CookieCategory } from '../utils/cookie';

type ToggleOptions = {
  value: string;
  enabled: boolean;
  readonly: boolean;
};
type CookieCategoryToggleOptions = { [key in CookieCategory]: ToggleOptions };
const cookieCategoryToggleOptions: CookieCategoryToggleOptions = {
  necessary: {
    enabled: true,
    readonly: true,
    value: 'necessary',
  },
  functional: {
    enabled: false,
    readonly: false,
    value: 'functional',
  },
  analytics: {
    enabled: false,
    readonly: false,
    value: 'analytics',
  },
};
const useLanugageSetting = (lng: 'de' | 'en' | 'cimode') => {
  const { t, i18n } = useTranslation(['cookieBanner']);

  // `ReactDOMServer.renderToStaticMarkup` can not nbe used inside of a `useMemo` callback,
  // so we render it each time and use the resulting string instead
  const weUseCookiesLink = ReactDOMServer.renderToStaticMarkup(
    Trans({
      t,
      i18nKey: `cookieBanner:text.WirVerwendenCookiesUmIhrErlebnisAufUnsererWebsiteSoAngenehmWieMoglicXX`,
      tOptions: { lng },
      components: {
        privacy: (
          <Link
            aria-label="privacy policy"
            href={externalRoutes.privacy()}
            rel="noreferrer"
            target="_blank"
          />
        ),
      },
    })
  );

  // `ReactDOMServer.renderToStaticMarkup` can not nbe used inside of a `useMemo` callback,
  // so we render it each time and use the resulting string instead
  const moreinformationLink = ReactDOMServer.renderToStaticMarkup(
    Trans({
      t,
      i18nKey: `cookieBanner:text.FurWeitereInformationenZumThemaDatenschutzLiesUnserePrivacyDatenschuXX`,
      tOptions: { lng },
      components: {
        privacy: (
          <Link
            aria-label="privacy policy"
            href={externalRoutes.privacy()}
            rel="noreferrer"
            target="_blank"
          />
        ),
      },
    })
  );

  // Workaround for test environments,
  // so translations can are mapped to their tKey
  // and can be found that way
  if (i18n.language === 'cimode') {
    lng = 'cimode';
  }

  const languageSetting = React.useMemo(() => {
    return {
      consent_modal: {
        title: t('cookieBanner:heading.WirNutzenCookies', { lng }),
        description: weUseCookiesLink,

        primary_btn: {
          text: t('cookieBanner:button.AlleAkzeptieren', { lng }),
          role: 'accept_all', // 'accept_selected' or 'accept_all'
        },
        secondary_btn: {
          text: t('cookieBanner:button.Einstellungen', { lng }),
          role: 'settings', // 'settings' or 'accept_necessary'
        },
      },
      settings_modal: {
        title: t('cookieBanner:heading.CookieEinstellungen', { lng }),
        save_settings_btn: t('cookieBanner:button.Speichern', { lng }),
        accept_all_btn: t('cookieBanner:button.AlleAkzeptieren', { lng }),
        reject_all_btn: t('cookieBanner:button.AlleAblehnen', { lng }), // optional, [v.2.5.0 +]
        cookie_table_headers: [
          { col1: 'Name' },
          { col2: 'Domain' },
          { col3: 'Expiration' },
          { col4: 'Description' },
          { col5: 'Type' },
        ],
        blocks: [
          {
            title: t('cookieBanner:heading.CookieNutzung', { lng }),
            description: t(
              'cookieBanner:text.UnsereCookiesFallenInFolgendeKategorien',
              { lng }
            ),
          },
          {
            title: t('cookieBanner:heading.EssentielleCookies', { lng }),
            description: t(
              'cookieBanner:text.DieseCookiesSindNotwendigFurDenEinwandfreienTechnischenBetriebDerWebXX',
              { lng }
            ),
            toggle: cookieCategoryToggleOptions.necessary,
          },
          {
            title: t('cookieBanner:heading.FunktionaleCookies', { lng }),
            description: t(
              'cookieBanner:text.DieseCookiesVerbessernDieNutzererfahrungAufUnsererWebsiteIndemSieSicXX',
              { lng }
            ),
            toggle: cookieCategoryToggleOptions.functional,
          },
          {
            title: t('cookieBanner:heading.AnalyseUndOptimierung', { lng }),
            description: t(
              'cookieBanner:text.DieseCookiesWerdenVerwendetUmDieNutzungUnsererWebsiteMitZuAnalysiereXX',
              { lng }
            ),
            toggle: cookieCategoryToggleOptions.analytics,
          },
          {
            title: t('cookieBanner:heading.MehrInformationen', { lng }),
            description: moreinformationLink,
          },
        ],
      },
    } as LanguageSetting;
  }, [t, weUseCookiesLink, moreinformationLink, lng]);

  return languageSetting;
};
export const useCookieConsentLanguageOptions = () => {
  const { i18n } = useTranslation(['cookieBanner']);

  const de = useLanugageSetting('de');
  const en = useLanugageSetting('en');

  const current_lang = i18n.language;

  return React.useMemo(
    () => ({
      languages: {
        de,
        en,
      },
      current_lang,
    }),
    [de, en, current_lang]
  );
};
